/**
 * Contract discount, split by pricing term — the rules behind
 * ContractDiscountDialog, pure so they are tested without rendering.
 *
 * Why three values: the design (2026-10-21) replaces the one "Contract
 * Discount %" with On Demand, 1 Year RI and 3 Year RI, each a number from 0
 * to 99.90 with up to two decimals (legacy's field; see DISCOUNT_TERM_MAX).
 * Each may be left blank (no discount for that term), as the single field
 * could.
 *
 * The keys here are the UI's own; on the wire the three travel as ONE object,
 * `pricing_model_discount: { ondemand, reserved, reserved_3yr }` (backend,
 * 2026-10-21), which REPLACES the old single `msa_discount` in every request
 * and response. toPricingModelDiscount / fromPricingModelDiscount are the only
 * places that know the API's names.
 */

/** The field the backend sends and takes (replaces `msa_discount`). */
export const PRICING_MODEL_DISCOUNT = 'pricing_model_discount';

/** UI term key -> API key inside pricing_model_discount. */
const API_KEYS = Object.freeze({
  onDemand: 'ondemand',
  oneYearRi: 'reserved',
  threeYearRi: 'reserved_3yr',
});

export const DISCOUNT_TERMS = Object.freeze([
  // Wording from the user, 2026-10-23.
  Object.freeze({ key: 'onDemand', label: 'On-Demand Discount (%)' }),
  Object.freeze({ key: 'oneYearRi', label: '1-Year RI (%)' }),
  Object.freeze({ key: 'threeYearRi', label: '3-Year RI (%)' }),
]);

/*
 * The range and decimals of one term are legacy's Contract Discount% field
 * (ManageAccount.vue: min 0, max 99.90, step 0.01, "Max 2 decimal places
 * allowed"; user order 2026-10-26 — the design's whole numbers 0-99 of
 * 2026-10-21 are replaced by it).
 */
export const DISCOUNT_TERM_MIN = 0;
export const DISCOUNT_TERM_MAX = 99.9;
export const DISCOUNT_TERM_DECIMALS = 2;
/** The arrows' step: one hundredth, as legacy's input. */
export const DISCOUNT_TERM_STEP = 0.01;
export const DISCOUNT_TERM_HINT = `Enter a value between ${DISCOUNT_TERM_MIN} and ${DISCOUNT_TERM_MAX.toFixed(
  DISCOUNT_TERM_DECIMALS,
)}, up to ${DISCOUNT_TERM_DECIMALS} decimal places.`;
export const DISCOUNT_TERM_PLACEHOLDER = 'Enter a number';

/**
 * A term as written: "0" alone or 1-99 as the whole part, with no leading zero
 * (user order 2026-10-23: a first digit of 0 allows only a decimal point after
 * it — "011" and "0000000011" were accepted before because they ARE 11 as
 * numbers), then at most two decimals. A trailing "." is allowed so a decimal
 * can be typed one key at a time.
 */
const TERM_PATTERN = /^(0|[1-9]\d?)(\.\d{0,2})?$/;

/**
 * Has at least one term been typed (user order 2026-10-26)? A "0" counts — it
 * is a value; only a blank does not. Apply stays off until this is true.
 */
export function hasContractDiscountValue(draft) {
  return DISCOUNT_TERMS.some((t) => String(draft?.[t.key] ?? '').trim() !== '');
}

/** All three terms blank. */
export function emptyContractDiscount() {
  return Object.fromEntries(DISCOUNT_TERMS.map((t) => [t.key, '']));
}

/** A value from outside (numbers, nulls, missing keys) as the dialog's strings. */
export function toContractDiscountDraft(value) {
  return Object.fromEntries(
    DISCOUNT_TERMS.map((t) => {
      const v = value?.[t.key];
      return [t.key, v === null || v === undefined ? '' : String(v)];
    }),
  );
}

/**
 * One term's problem, or null. Blank is allowed; otherwise a number from 0 to
 * 99.90 with at most two decimals. The messages are legacy's (ManageAccount.vue
 * updateMsaDiscount), without its "Contract Discount%:" prefix.
 *
 * @returns {string | null}
 */
export function discountTermError(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const text = String(raw);
  const n = Number(text);
  if (!Number.isFinite(n)) return 'Must be a number';
  if (n < DISCOUNT_TERM_MIN) return `Must be ${DISCOUNT_TERM_MIN} or greater`;
  if (n > DISCOUNT_TERM_MAX) return `Must be ${DISCOUNT_TERM_MAX.toFixed(2)} or less`;
  if (new RegExp(`\\.\\d{${DISCOUNT_TERM_DECIMALS + 1},}$`).test(text)) {
    return `Max ${DISCOUNT_TERM_DECIMALS} decimal places allowed`;
  }
  // A leading zero ("05", "011") or anything else the field never lets in.
  if (!TERM_PATTERN.test(text)) return DISCOUNT_TERM_HINT;
  return null;
}

/**
 * Can `raw` go into a term field while typing? Blank, or a number no bigger
 * than 99.90 with at most two decimals (user order 2026-10-23: the range is
 * ENFORCED, not just reported). A keystroke or paste that would break it — a
 * third decimal, or past 99.90 — is refused, so the field keeps its last good
 * value, the same way the minus and 'e' keys are already refused. Values that
 * arrive from outside (a saved portfolio) are not typed, so discountTermError
 * still reports those.
 */
export function acceptsTermInput(raw) {
  const text = String(raw ?? '');
  return text === '' || (TERM_PATTERN.test(text) && Number(text) <= DISCOUNT_TERM_MAX);
}

/** Do all three terms hold the same value (all blank counts)? */
export function isSameAcrossTerms(draft) {
  const values = DISCOUNT_TERMS.map((t) => String(draft?.[t.key] ?? ''));
  return values.every((v) => v === values[0]);
}

/**
 * "Apply same contract discount to all pricing models": every term set to
 * `value`. Without a value, the first term that has one (On Demand, then
 * 1 Year RI, then 3 Year RI) — so ticking the box copies what is already
 * typed instead of wiping it. All blank stays all blank.
 */
export function fillAllTerms(draft, value) {
  const source =
    value ?? DISCOUNT_TERMS.map((t) => String(draft?.[t.key] ?? '')).find((v) => v !== '') ?? '';
  return Object.fromEntries(DISCOUNT_TERMS.map((t) => [t.key, String(source)]));
}

/**
 * The button that opens the dialog: "Contract discount: 10 / 15 / 20%" (On
 * Demand / 1 Year RI / 3 Year RI; a blank term shows as 0), or just "Contract
 * discount" while no term is set (user order 2026-10-21).
 */
export function contractDiscountLabel(value) {
  const draft = toContractDiscountDraft(value);
  const values = DISCOUNT_TERMS.map((t) => draft[t.key]);
  if (values.every((v) => v === '' || Number(v) === 0)) return 'Contract discount';
  return `Contract discount: ${values.map((v) => (v === '' ? '0' : v)).join(' / ')}%`;
}

const blankTerm = (v) => v === '' || v === null || v === undefined;

/** Is no term set (all blank or 0)? A missing value counts as none. */
export function isContractDiscountEmpty(value) {
  const draft = toContractDiscountDraft(value);
  return DISCOUNT_TERMS.every((t) => blankTerm(draft[t.key]) || Number(draft[t.key]) === 0);
}

/**
 * Same discount? Term by term, blank and 0 are equal (a blank is sent as 0,
 * so it comes back as 0) and "5" equals 5.
 */
export function sameContractDiscount(a, b) {
  const x = toContractDiscountDraft(a);
  const y = toContractDiscountDraft(b);
  const asNumber = (v) => (blankTerm(v) ? 0 : Number(v));
  return DISCOUNT_TERMS.every((t) => asNumber(x[t.key]) === asNumber(y[t.key]));
}

/**
 * The UI value as the API takes it:
 * { ondemand, reserved, reserved_3yr }, each a number, a blank term as 0.
 */
export function toPricingModelDiscount(value) {
  const draft = toContractDiscountDraft(value);
  return Object.fromEntries(
    DISCOUNT_TERMS.map((t) => {
      const v = draft[t.key];
      const n = blankTerm(v) ? 0 : Number(v);
      return [API_KEYS[t.key], Number.isFinite(n) ? n : 0];
    }),
  );
}

/**
 * The discount as GET /upload-portfolio takes it (backend curl, 2026-10-21):
 * FLAT query keys, not the pricing_model_discount object — a query string
 * cannot carry an object. A blank term is 0.
 */
export function toUploadDiscountParams(value) {
  const { ondemand, reserved, reserved_3yr: reserved3yr } = toPricingModelDiscount(value);
  return {
    ondemand_model_discount: ondemand,
    reserved_model_discount: reserved,
    reserved_3yr_model_discount: reserved3yr,
    // msa_discount is in the backend's curl, but it is for a third party, not
    // this app (user, 2026-10-21) — not sent. Kept here in case it is needed:
    // msa_discount: ondemand,
  };
}

/**
 * The API's pricing_model_discount as the UI value (strings, '' for a term
 * the server left out or sent as null), or null when the server sent none.
 */
export function fromPricingModelDiscount(api) {
  if (api === null || api === undefined || typeof api !== 'object') return null;
  return Object.fromEntries(
    DISCOUNT_TERMS.map((t) => {
      const v = api[API_KEYS[t.key]];
      return [t.key, v === null || v === undefined ? '' : String(v)];
    }),
  );
}

/** { [termKey]: message } for the terms with a problem; {} when all are fine. */
export function contractDiscountErrors(draft) {
  const errors = {};
  for (const { key } of DISCOUNT_TERMS) {
    const error = discountTermError(draft?.[key]);
    if (error) errors[key] = error;
  }
  return errors;
}

/**
 * The first problem as one line for a form's Save check, or null — e.g.
 * "1-Year RI (%): Enter a value between 0 and 99.".
 */
export function contractDiscountProblem(value) {
  const errors = contractDiscountErrors(toContractDiscountDraft(value));
  const term = DISCOUNT_TERMS.find((t) => errors[t.key]);
  return term ? `${term.label}: ${errors[term.key]}` : null;
}
